<svelte:options runes={false} />
<script>
  // Cumulative savings (TASK-20260823-twap-strategies-tca PR-C2, D9): the running total of net savings
  // vs sweep over the strategy's finished parents, in EndedAt order — an SVG step line with the area
  // to the zero baseline. Sign is carried by the signed headline (sign + arrow), the baseline side AND
  // the tone, never color alone. A parent with no scorecard is never drawn as a zero step; the
  // readout says how many were skipped. Every marker is a focusable button with a readout so a
  // keyboard user can read each step.
  import { timeScale, linearScale, niceDomain, stepPath, areaPath, pad, formatTick, formatTimeTick } from '../charts/svgScales.js';
  import { fmtQuote } from '../../../lib/twapReport.js';

  export let series = null;     // from cumulativeSavingsSeries(parents)
  export let currency = 'USD';

  const H = 180;
  const PAD = { top: 14, right: 14, bottom: 26, left: 56 };
  let width = 320;
  let active = null;
  let pinned = null;
  function togglePin(p) { pinned = pinned === p ? null : p; active = pinned ?? p; }
  function onMarkerKey(e, p) {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); togglePin(p); }
  }

  $: points = series?.points ?? [];
  $: hasData = points.length > 0;
  $: total = series?.total ?? null;
  $: tone = total === null || total === 0 ? 'neutral' : total > 0 ? 'gain' : 'loss';
  $: w = Math.max(220, width);
  $: tDomain = hasData ? pad([points[0].t, points[points.length - 1].t], 0.03) : [0, 1];
  $: x = timeScale(tDomain, [PAD.left, w - PAD.right]);
  $: values = points.map((p) => p.cumulative);
  $: yDomain = hasData ? niceDomain(Math.min(0, ...values), Math.max(0, ...values), 4) : [0, 1];
  $: y = linearScale(yDomain, [H - PAD.bottom, PAD.top]);
  // Step-after from an origin at zero: the total is 0 until the first parent finishes.
  $: stepPts = hasData ? [{ t: points[0].t, cumulative: 0 }, ...points] : [];
  $: line = stepPath(stepPts, (p) => x(p.t), (p) => y(p.cumulative));
  // The area follows the same step: duplicate each point at the next x so the fill is stepped too.
  $: areaPts = stepPts.flatMap((p, i) => (i < stepPts.length - 1 ? [p, { t: stepPts[i + 1].t, cumulative: p.cumulative }] : [p]));
  $: area = areaPath(areaPts, (p) => x(p.t), (p) => y(p.cumulative), () => y(0));
  $: yTicks = y.ticks(4);
  $: xTicks = x.ticks(Math.max(2, Math.floor(w / 110)));

  function readout(p) {
    return `${p.symbol ?? '?'} ${p.side ?? ''} finished ${new Date(p.t).toLocaleString()}: ${fmtQuote(p.delta, currency)} → running total ${fmtQuote(p.cumulative, currency)}`;
  }
  $: summaryText = hasData
    ? `Cumulative net savings ${fmtQuote(total, currency)} over ${series.count} finished parent${series.count === 1 ? '' : 's'}${series.skipped ? ` (${series.skipped} without a scorecard skipped)` : ''}`
    : 'No finished, scored parents yet.';
</script>

<div class="cs" bind:clientWidth={width}>
  {#if !hasData}
    <p class="cs-empty">{summaryText}{#if series?.skipped}<span class="cs-sub"> — {series.skipped} parent{series.skipped === 1 ? '' : 's'} still without a scorecard.</span>{/if}</p>
  {:else}
    <p class={`cs-head tone-${tone}`}>
      <span class="cs-total num">{fmtQuote(total, currency)}</span>
      <span class="cs-sub">over {series.count} finished parent{series.count === 1 ? '' : 's'}{#if series.skipped} · {series.skipped} skipped (no scorecard){/if}</span>
    </p>
    <svg class="cs-svg" viewBox={`0 0 ${w} ${H}`} width="100%" height={H} role="img" aria-label={summaryText}>
      {#each yTicks as t}
        <line x1={PAD.left} x2={w - PAD.right} y1={y(t)} y2={y(t)} class="cs-grid" />
        <text x={PAD.left - 6} y={y(t) + 3} class="cs-tick" text-anchor="end">{formatTick(t)}</text>
      {/each}
      {#each xTicks as t}
        <text x={x(t)} y={H - PAD.bottom + 14} class="cs-tick" text-anchor="middle">{formatTimeTick(t)}</text>
      {/each}
      <line x1={PAD.left} x2={w - PAD.right} y1={y(0)} y2={y(0)} class="cs-zero" />
      <path d={area} class={`cs-area tone-${tone}`} />
      <path d={line} class={`cs-line tone-${tone}`} />
      {#each points as p (p.id)}
        <circle cx={x(p.t)} cy={y(p.cumulative)} r="4" class={`cs-marker ${p.delta >= 0 ? 'cs-marker-gain' : 'cs-marker-loss'}`}
          tabindex="0" role="button" aria-label={readout(p)} aria-pressed={pinned === p}
          on:mouseenter={() => (active = p)} on:mouseleave={() => (active = pinned)}
          on:focus={() => (active = p)} on:blur={() => (active = pinned)}
          on:click={() => togglePin(p)} on:keydown={(e) => onMarkerKey(e, p)}>
          <title>{readout(p)}</title>
        </circle>
      {/each}
    </svg>
    <p class="cs-readout num" aria-live="polite">{active ? readout(active) : 'Hover or focus a step for its parent.'}</p>
  {/if}
</div>

<style>
  .cs { width: 100%; min-width: 0; }
  .num { font-variant-numeric: tabular-nums; }
  .cs-head { margin: 0 0 0.3rem; display: flex; align-items: baseline; gap: 0.5rem; flex-wrap: wrap; }
  .cs-total { font-size: var(--font-size-lg, 1.1rem); font-weight: var(--font-weight-semibold, 600); color: var(--text-primary, #fff); }
  .cs-head.tone-gain .cs-total { color: var(--color-success, #00ff88); }
  .cs-head.tone-loss .cs-total { color: var(--color-danger, #ff3b3b); }
  .cs-sub, .cs-empty { font-size: var(--font-size-xs, 0.7rem); color: var(--text-muted, #6a6b6d); }
  .cs-empty { margin: 0; font-size: var(--font-size-sm, 0.85rem); }
  .cs-svg { display: block; border: 1px solid var(--border-primary, #1e1f22); border-radius: var(--radius-base, 0.5rem); background: var(--bg-secondary, #111214); }
  .cs-grid { stroke: var(--chart-grid, #1e1f22); stroke-width: 1; }
  .cs-zero { stroke: var(--text-muted, #6a6b6d); stroke-width: 1; }
  .cs-tick { fill: var(--text-secondary, #8a8b8d); font-size: 10px; font-variant-numeric: tabular-nums; }
  .cs-line { fill: none; stroke: var(--color-primary, #00d4ff); stroke-width: 1.5; }
  .cs-line.tone-gain { stroke: var(--color-success, #00ff88); }
  .cs-line.tone-loss { stroke: var(--color-danger, #ff3b3b); }
  .cs-area { fill: var(--color-primary, #00d4ff); opacity: 0.14; }
  .cs-area.tone-gain { fill: var(--color-success, #00ff88); }
  .cs-area.tone-loss { fill: var(--color-danger, #ff3b3b); }
  .cs-marker { fill: var(--bg-secondary, #111214); stroke-width: 1.5; cursor: pointer; outline: none; transition: r var(--transition-fast, 120ms ease); }
  .cs-marker-gain { stroke: var(--color-success, #00ff88); }
  .cs-marker-loss { stroke: var(--color-danger, #ff3b3b); }
  .cs-marker:hover, .cs-marker:focus-visible { r: 6; stroke-width: 3; }
  .cs-marker:focus-visible { stroke: var(--color-primary, #00d4ff); }
  .cs-readout { margin: 0.3rem 0 0; font-size: var(--font-size-xs, 0.7rem); color: var(--text-secondary, #8a8b8d); min-height: 1.2em; }
  @media (prefers-reduced-motion: reduce) { .cs-marker { transition: none; } }
</style>
